<template>
  <div class="bg-primary">
    <!-- Header -->
    <div class="header bg-gradient-blue py-7 py-lg-6 pt-lg-5">
      <b-container>
        <div class="header-body text-center mb-5">
          <b-row class="justify-content-center">
            <b-col xl="5" lg="6" md="8" class="px-5">
              <h1 class="text-white">
                <span>Accept invitation</span>
              </h1>
              <p class="text-lead text-white">
                <span v-if="!loading">
                  Please use the form below to accept this invitation.
                </span>
                <span v-else>
                  <i class="fa fa-spin fa-spinner mr-2"></i>
                  Getting invitation details&hellip;
                </span>
              </p>
            </b-col>
          </b-row>
        </div>
      </b-container>
      <div class="separator separator-bottom separator-skew zindex-100">
        <svg
          x="0"
          y="0"
          viewBox="0 0 2560 100"
          preserveAspectRatio="none"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon class="fill-primary" points="2560 0 2560 100 0 100" />
        </svg>
      </div>
    </div>
    <!-- Page content -->
    <b-container class="mt--7 pb-5">
      <b-row class="justify-content-center">
        <b-col lg="5" md="7">
          <b-card no-body class="bg-secondary border-0 mb-0">
            <b-card-body class="px-lg-5 py-lg-5" v-if="loading">
              <p class="text-lg text-center">
                <i class="fa fa-spin fa-spinner mr-2"></i>
                Please wait&hellip;
              </p>
            </b-card-body>

            <b-card-body
              class="px-lg-5 py-lg-5"
              v-if="!loading && !invitation && errorMessage"
            >
              <base-alert
                v-if="errorMessage"
                :dismissible="false"
                type="default"
                >Error: {{ errorMessage }}</base-alert
              >
            </b-card-body>

            <b-card-body class="px-lg-5 py-lg-5" v-if="!loading && invitation">
              <b-row align-v="center">
                <b-col cols="3">
                  <b-img
                    class="img-fluid"
                    rounded="circle"
                    alt="Profile picture"
                    :src="invitation.creator.avatarUrl"
                  />
                </b-col>
                <b-col cols="9">
                  <span v-html="invitation.creator.displayName"></span> invited
                  you to their account
                  <span v-html="invitation.account.name"></span>
                </b-col>
              </b-row>
              <div class="text-center text-muted mb-4 mt-2">
                <small v-if="user"
                  >Use the button below to accept the invitation.</small
                ><small v-else
                  >Please use the form below to accept the invitation and create
                  your user account.</small
                >
              </div>

              <base-alert
                v-if="errorMessage"
                :dismissible="false"
                type="default"
                >Error: {{ errorMessage }}</base-alert
              >

              <div v-if="user">
                <div class="text-center">
                  <base-button
                    type="primary"
                    @click="acceptInvitationAsExistingUser"
                    class="my-4 btn-block"
                  >
                    <span v-if="!accepting">
                      Join <span v-html="invitation.account.name"></span>
                      <i class="fa fa-arrow-right ml-2"></i>
                    </span>
                    <span v-else>
                      Please wait&hellip;
                      <i class="fa fa-spin fa-spinner ml-2"></i>
                    </span>
                  </base-button>

                  <p class="text-muted text-sm">
                    Joining as <span v-html="user.displayName"></span>
                    <span v-if="user.displayName !== user.email.address"
                      >({{ user.email.address }})</span
                    ><br />
                    <b-btn
                      @click="logoutAndRedirect"
                      variant="link"
                      size="sm"
                      :disabled="accepting"
                    >
                      Log in as different user
                      <i class="fa fa-user-alt ml-2"></i>
                    </b-btn>
                  </p>
                </div>
              </div>

              <validation-observer
                v-if="!user"
                v-slot="{ handleSubmit }"
                ref="formValidator"
              >
                <b-form
                  role="form"
                  @submit.prevent="handleSubmit(acceptInvitationAsNewUser)"
                >
                  <base-input
                    alternative
                    class="mb-3"
                    name="Email"
                    :rules="{ required: true, email: true }"
                    :debounce="500"
                    prepend-icon="ni ni-email-83"
                    placeholder="Email address"
                    v-model="model.email"
                  ></base-input>

                  <base-input
                    alternative
                    class="mb-3"
                    name="Password"
                    :rules="{ required: true }"
                    :debounce="500"
                    prepend-icon="ni ni-lock-circle-open"
                    type="password"
                    placeholder="Password"
                    v-model="model.password"
                  ></base-input>

                  <b-form-checkbox v-model="model.staySignedIn"
                    >Stay logged in</b-form-checkbox
                  >

                  <div class="text-center">
                    <base-button
                      type="primary"
                      native-type="submit"
                      class="my-4 btn-block"
                    >
                      <span v-if="!accepting">
                        Join account
                        <i class="fa fa-arrow-right ml-2"></i>
                      </span>
                      <span v-else>
                        Please wait&hellip;
                        <i class="fa fa-spin fa-spinner ml-2"></i>
                      </span>
                    </base-button>
                  </div>

                  <p class="text-sm text-muted">
                    Already have an Admin Hero user account?
                    <router-link
                      :to="`/login?redirect=${encodeURIComponent(
                        `/accept-account-invitation/${invitation.id}?token=${token}&accountId=${accountId}`
                      )}`"
                      >Log in</router-link
                    >
                  </p>
                </b-form>
              </validation-observer>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import { BaseAlert } from "@/components";
import { get } from "dot-prop";

export default {
  components: {
    BaseAlert,
  },
  props: {
    invitationId: {
      type: String,
      description: "Invitation ID",
      required: true,
    },
    accountId: {
      type: String,
      description: "Account ID",
      required: true,
    },
    token: {
      type: String,
      description: "Invitation Token",
      required: true,
    },
  },
  data() {
    return {
      model: {
        email: "",
        password: "",
        staySignedIn: true, // by default
      },
      accepting: false,
      loading: false,
      errorMessage: null,
      invitation: null,
    };
  },
  computed: {
    user() {
      return this.$auth.user();
    },
  },
  created() {
    this.errorMessage = null;
    this.loading = true;
    // load the invitation
    const params = {};
    if (!this.user) {
      // supply the verification token
      params.verificationToken = this.token;
    }
    this.axios
      .get(`/v1/accounts/${this.accountId}/invitations/${this.invitationId}`, {
        params,
      })
      .then((response) => {
        this.loading = false;
        this.invitation = response.data;
        this.model.email = this.invitation.emailAddress;

        // check not expired or already accepted
        if (this.invitation.isAccepted === true) {
          this.errorMessage = "Invitation has already been accepted.";
          this.invitation = null;
        } else if (this.$moment(this.invitation.expiresAt).isBefore()) {
          this.errorMessage = "Invitation has expired.";
          this.invitation = null;
        }
      })
      .catch((error) => {
        this.loading = false;
        this.errorMessage = `Error loading invitation: ${error.response.statusText}`;
        if (get(error, "response.data.message")) {
          this.errorMessage = error.response.data.message;
        }
      });
  },
  methods: {
    logoutAndRedirect: function () {
      this.$auth
        .logout({
          makeRequest: false,
          redirect: `/accept-account-invitation/${this.invitationId}?token=${this.token}&accountId=${this.accountId}`,
        })
        .then(() => {
          // clear any local storage
          this.$ls.clear();
          // clear from store
          this.$store.commit("SET_ACCOUNT", null);
          this.$store.commit("SET_MEMBERSHIP", null);
        })
        .catch((err) => {
          console.error("Error after logout(): ", err);
        });
    },
    acceptInvitationAsExistingUser: function () {
      this.accepting = true;
      this.axios
        .post(
          `/v1/accounts/${this.accountId}/invitations/${this.invitationId}/acceptance`,
          { verificationToken: this.token }
        )
        .then((response) => {
          this.accepting = false;

          this.$notify({
            message: "Invitation accepted",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success",
          });

          this.$router.push("/select-account");
        })
        .catch((error) => {
          this.accepting = false;
          this.errorMessage = `Error accepting invitation: ${error.response.statusText}`;
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    acceptInvitationAsNewUser: function () {
      // this will be called only after form is valid.
      this.errorMessage = null; // reset error
      this.accepting = true;

      const requestBody = { verificationToken: this.token };

      requestBody.user = {
        emailAddress: this.model.email,
        password: this.model.password,
      };

      this.axios
        .post(
          `/v1/accounts/${this.accountId}/invitations/${this.invitationId}/acceptance`,
          requestBody
        )
        .then((response) => {
          this.accepting = false;

          this.$notify({
            message: "Invitation accepted",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success",
          });

          // then log the user in using form inputs
          return this.$auth.login({
            data: {
              emailAddress: this.model.email,
              password: this.model.password,
            },
            staySignedIn: this.model.staySignedIn,
            redirect: "/select-account",
          });
        })
        .catch((error) => {
          this.accepting = false;
          this.errorMessage = `Error accepting invitation: ${error.response.statusText}`;
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
  },
};
</script>
